'use client';

import { type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import { SIcon, type SIconName } from '../SIcon';

const PER_PAGE = 10;
const BUTTON_WIDTH: Record<number, number> = { 1: 24, 2: 34, 3: 40, 4: 48, 5: 56 };

export interface SPaginationProps {
  /** 현재 페이지 */
  currentPage?: number;
  /** 마지막 페이지 */
  lastPage?: number;
  /** 단순 모드 (현재/마지막만 표시) */
  simple?: boolean;
  /** 페이지 변경 (sdPageChange) */
  onPageChange?: (page: number) => void;
  className?: string;
  style?: CSSProperties;
}

/** SPagination — sd-pagination 포팅. 10개 단위 그룹 이동 + simple 모드. */
export function SPagination({
  currentPage = 1,
  lastPage = 1,
  simple = false,
  onPageChange,
  className,
  style,
}: SPaginationProps) {
  const start = Math.floor((currentPage - 1) / PER_PAGE) * PER_PAGE + 1;
  const end = Math.min(start + PER_PAGE - 1, lastPage);
  const pageNumbers = Array.from({ length: Math.max(end - start + 1, 0) }, (_, i) => start + i);
  const maxDigits = (pageNumbers.length > 0 ? pageNumbers[pageNumbers.length - 1] : 1).toString()
    .length;
  const itemWidth = BUTTON_WIDTH[maxDigits] || BUTTON_WIDTH[1];

  const isFirstGroup = currentPage <= PER_PAGE;
  const isLastGroup = start + PER_PAGE - 1 >= lastPage;

  const go = (page: number) => {
    if (page < 1 || page > lastPage || page === currentPage) return;
    onPageChange?.(page);
  };
  const groupMove = (dir: 'forward' | 'backward') => {
    const delta = dir === 'forward' ? PER_PAGE : -PER_PAGE;
    go(Math.min(Math.max(currentPage + delta, 1), lastPage));
  };

  const navBtn = (icon: SIconName, aria: string, onClick: () => void) => (
    <button
      type="button"
      aria-label={aria}
      onClick={onClick}
      className="flex h-[var(--cmp-pagination-height)] w-[var(--cmp-pagination-height)] cursor-pointer items-center justify-center rounded-[var(--cmp-pagination-radius)] border-none bg-transparent hover:bg-[var(--cmp-pagination-item-bg-hover)]"
    >
      <SIcon name={icon} size={12} color="var(--color-grey-65)" />
    </button>
  );

  const prevButtons = simple
    ? currentPage > 1 && (
        <>
          {navBtn('chevronLeftEnd', '첫 페이지', () => go(1))}
          {navBtn('chevronLeft', '이전 페이지', () => go(currentPage - 1))}
        </>
      )
    : !isFirstGroup && (
        <>
          {navBtn('chevronLeftEnd', '첫 페이지', () => go(1))}
          {navBtn('chevronLeft', '이전 그룹', () => groupMove('backward'))}
        </>
      );

  const nextButtons = simple
    ? currentPage < lastPage && (
        <>
          {navBtn('chevronRight', '다음 페이지', () => go(currentPage + 1))}
          {navBtn('chevronRightEnd', '마지막 페이지', () => go(lastPage))}
        </>
      )
    : !isLastGroup && (
        <>
          {navBtn('chevronRight', '다음 그룹', () => groupMove('forward'))}
          {navBtn('chevronRightEnd', '마지막 페이지', () => go(lastPage))}
        </>
      );

  return (
    <div
      className={cn('inline-flex items-center gap-[var(--cmp-pagination-gap)]', className)}
      style={style}
    >
      <div className="flex min-w-[calc(var(--cmp-pagination-height)*2+var(--cmp-pagination-move-gap))] items-center justify-end gap-[var(--cmp-pagination-move-gap)]">
        {prevButtons}
      </div>

      {simple ? (
        <div className="flex h-[var(--cmp-pagination-height)] items-center gap-[var(--cmp-pagination-simple-gap)] px-[var(--cmp-pagination-paddingX)] text-[12px] font-medium leading-[20px] text-[color:var(--cmp-pagination-item-content-default)]">
          <span className="font-bold text-[color:var(--cmp-pagination-item-content-selected)]">
            {currentPage}
          </span>
          <span>/</span>
          <span>{lastPage}</span>
        </div>
      ) : (
        <div className="flex items-center gap-[var(--cmp-pagination-move-gap)]">
          {pageNumbers.map(n => {
            const selected = currentPage === n;
            return (
              <button
                key={n}
                type="button"
                aria-current={selected ? 'page' : undefined}
                onClick={() => go(n)}
                className={cn(
                  'box-border flex h-[var(--cmp-pagination-height)] cursor-pointer items-center justify-center rounded-[var(--cmp-pagination-radius)] border-none px-[var(--cmp-pagination-paddingX)] text-[12px] leading-[20px]',
                  selected
                    ? 'bg-[var(--cmp-pagination-item-bg-selected)] font-bold text-[color:var(--cmp-pagination-item-content-selected)]'
                    : 'bg-transparent font-medium text-[color:var(--cmp-pagination-item-content-default)] hover:bg-[var(--cmp-pagination-item-bg-hover)] hover:text-[color:var(--cmp-pagination-item-content-hover)]',
                )}
                style={{ width: itemWidth }}
              >
                {n}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex min-w-[calc(var(--cmp-pagination-height)*2+var(--cmp-pagination-move-gap))] items-center justify-start gap-[var(--cmp-pagination-move-gap)]">
        {nextButtons}
      </div>
    </div>
  );
}
